import { defaultStoreUtils } from '@databases/redux';
import {
  getDatabaseWithCurrentView,
  selectHasSelectedCells,
  selectSelectedValues,
} from '@databases/redux/cellSelections/selectors';
import { DatabaseFieldType } from '@databases/types/database';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import useView from '@databases/views/useView';
import { useCallback, useEffect } from 'react';

import { copyHtmlTableToClipboard } from '../helpers/copy-helper';

import { useCopyWithDsp } from './useCopyWithDsp';

interface UseCopyDatabaseContentProps {
  contentId: string;
}

/**
 * Registers a document event listener that reacts to copy events.
 * This is used to copy the selected database content to the clipboard.
 */
export const useCopyListener = ({ contentId }: UseCopyDatabaseContentProps) => {
  const { view } = useView();
  const store = defaultStoreUtils.useStore();
  const { attemptCopy } = useCopyWithDsp();

  const hasSelection = defaultStoreUtils.useEqualSelector((state) => {
    return selectHasSelectedCells(state, {
      contentId,
    });
  });

  const areNonEmptyAttachmentsSelected = useCallback(() => {
    if (contentId) {
      const state = store.getState();
      const database = getDatabaseWithCurrentView(state, {
        contentId,
      });

      const selectedValues = selectSelectedValues(state, { contentId });
      const selectedAttachmentValues = selectedValues.filter(
        (value) =>
          getDatabaseFieldRequired(database, value.fieldId).type ===
          DatabaseFieldType.ATTACHMENT
      );

      return selectedAttachmentValues.some((selectedValue) => {
        const field = getDatabaseFieldRequired(database, selectedValue.fieldId);
        const entry = getDatabaseEntryRequired(database, selectedValue.entryId);
        const value = getDatabaseValue(database, field, entry);
        return !isEmptyValue({ database, field, entry, value }, state);
      });
    }
    return false;
  }, [contentId, store]);

  const handleExportDatabaseContent = useCallback(() => {
    copyHtmlTableToClipboard(contentId, store.getState(), view.settings);
  }, [contentId, store, view.settings]);

  const callback = useCallback(
    (e: ClipboardEvent) => {
      if (getSelection()?.type !== 'Range') {
        e.preventDefault();
        handleExportDatabaseContent();
      }
    },
    [handleExportDatabaseContent]
  );

  const copyListener = useCallback(
    (e: ClipboardEvent) => {
      attemptCopy(() => {
        callback(e);
      }, areNonEmptyAttachmentsSelected());
    },
    [attemptCopy, callback, areNonEmptyAttachmentsSelected]
  );

  useEffect(() => {
    if (!hasSelection) {
      return;
    }
    document.addEventListener('copy', copyListener);
    return () => {
      document.removeEventListener('copy', copyListener);
    };
  }, [copyListener, handleExportDatabaseContent, hasSelection]);
};
